




























































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {VueMaskDirective} from 'v-mask';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {isEqual} from 'lodash';
import {HTML_SERVICE_INSTANCE} from '@/services/html.service';
import moment from 'moment/moment';
import {FieldStyleContext} from '@/fields/field-style-context';

interface WeekNumberPreset {
  label: string;
  calculateValue: () => string;
}

/**
 * General Component for Fields of type DATE
 *
 * https://confluence.ametras.com/x/L4RQAw
 *
 * attachRelatively:
 *  - true: attaches the dialog relatively to the component wrapper
 *          This is needed in case of a position:fixed surrounding panel (usually NOT)
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  directives: {
    VueMaskDirective
  },
  mixins: [BindAttrs]
})
export default class AmetrasDateWeekNumberFieldTemplate extends Vue {
  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop({
    required: false,
    default: 'WW-GGGG'
  }) dateFormat!: string;
  @Prop({
    required: false,
    default: 'gggg-[W]ww'
  }) isoFormat!: string;
  @Prop({
    required: false,
    default: 'YYYY-MM-DD'
  }) isoPickerFormat!: string;
  @Prop({
    required: false
  }) value!: string;
  @Prop({
    required: false,
    default: false
  }) monthPicker!: boolean;
  @Prop({
    required: false,
    default: false
  }) required!: boolean;
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() hideDefaultIcon!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop() readonly!: boolean;
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() styleContext!: FieldStyleContext;
  showPicker = false;
  localeFormattedTextFieldValue = ''; // depending on the locale, e.g. (de) 12.03.2019
  isoFormattedDatepickerValue = ''; // iso formatted datepicker value, e.g. 2019-05
  mask = '';
  listeners: Record<string, any> = {};
  presets: WeekNumberPreset[] = [{
    label: 'Letzte Woche',
    calculateValue: () => {
      return moment().subtract(1, 'weeks').format(this.isoFormat);
    }
  }, {
    label: 'Aktuelle Woche',
    calculateValue: () => {
      return moment().format(this.isoFormat);
    }
  }, {
    label: 'Nächste Woche',
    calculateValue: () => {
      return moment().add(1, 'weeks').format(this.isoFormat);
    }
  }];

  dateRules = [(value: string) => {
    // validates the field value
    if (value && (this.forceShowErrors || !this.readonly)) {
      const isValidDate = this.$moment(value, this.dateFormat, true).isValid();
      if (!isValidDate) {
        return 'ungültige Eingabe (' + this.dateFormat + ')';
      }
    } else if (!value && this.required && (this.forceShowErrors || !this.readonly)) {
      return 'Datum fehlt';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];

  created() {

    if (this.value) {
      // if somebody provided a value from outside
      this.updateInternalModel(this.value);
    }
    // based on the current locale
    // this will create a mask, e.g. xx.xx.xxxx (representing masked DD.MM.YYYY)
    this.mask = this.dateFormat.replace(/[mdywg]/gi, '#');

    if (this.$listeners.input) {
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) {
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }
    this.listeners = {
      ...this.$listeners,
      input: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalInputListeners) {
            fnc(valueToBePublished);
          }
        });
      },
      change: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalChangeListeners) {
            fnc(valueToBePublished);
          }
        });
      }
    };
  }

  @Watch('value') modelChanges(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // if somebody provided a new value from outside
      this.updateInternalModel(newValue);
      this.refreshValidation();
    }
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('showPicker') pickerVisible(newValue: string) {
    if (newValue) {
      this.$emit('focus');
    } else {
      this.$emit('blur');
    }
  }

  @Watch('isoFormattedDatepickerValue') onDatePicked(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // someone picked a value via picker
      const valueAsMoment = this.$moment(newValue, this.isoPickerFormat, true);
      if (valueAsMoment.isValid()) {
        const formattedDate = valueAsMoment.format(this.isoFormat);
        if (!isEqual(formattedDate, this.value)) {
          for (const fnc of (this.originalChangeListeners || []).concat(this.originalInputListeners || [])) {
            fnc(formattedDate);
          }
        }
        this.localeFormattedTextFieldValue = valueAsMoment.format(this.dateFormat);
        Vue.nextTick(() => {
          this.refreshValidation();
        });
      }
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  afterDatePicked() {
    this.showPicker = false;
    setTimeout(() => {
      // with minimum delay, focus the field again
      HTML_SERVICE_INSTANCE.focusFirstNode(this.$el);
    });
  }

  captureEscKey(event: Event) {
    if (this.showPicker) {
      // prevent blubbling of escape event if datepicker is shown
      event.stopPropagation();
      this.showPicker = false;
    }
  }

  activatePreset(preset: WeekNumberPreset) {
    this.updateInternalModel(preset.calculateValue());
  }

  private updateInternalModel(newValue: string) {
    const parsedMomentFromValue = this.$moment(newValue, this.isoFormat, true);
    if (parsedMomentFromValue.isValid()) {
      this.localeFormattedTextFieldValue = parsedMomentFromValue.format(this.dateFormat);
      this.isoFormattedDatepickerValue = parsedMomentFromValue.format(this.isoPickerFormat);
    } else {
      this.localeFormattedTextFieldValue = '';
      this.isoFormattedDatepickerValue = '';
    }
  }

  private applyModelValueIfValid(newValue: string, validCb: (valueToBePublished: string | null) => void) {
    const valueAsMoment = this.$moment(newValue, this.dateFormat, true);
    if (valueAsMoment.isValid()) {
      this.isoFormattedDatepickerValue = valueAsMoment.format(this.isoPickerFormat);
      validCb(valueAsMoment.format(this.isoFormat));
    } else if (newValue === '') {
      validCb(newValue);
    }
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

}
